import { api } from './api';
import type {
  BrandQuery,
  BrandQueryAxis,
  BrandQueryStatus,
  BulkRequest,
  BulkResponse,
  BulkClassified,
  AllowanceFigures,
  PromptLastRun,
  SaveWarnings,
  TranslateVariantsRequest,
  TranslateVariantsResponse,
  CreateLanguageVariantsRequest,
  CreateLanguageVariantsResponse,
  CreateZoneVariantsRequest,
  CreateZoneVariantsResponse,
} from '@traaker/shared';
import type { Cadence } from '@traaker/shared';

export interface GeneratedQueryProposal {
  text: string;
  textHash: string;
  level?: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'pending';
  zone?: string;
  language?: string;
  /** Source group: authority topic (authority) or use case (referral). */
  topic?: string;
}

export interface QueryExampleRow {
  topic: string;
  narrative: string;
  authority: string;
  referral: string;
}

export const brandQueriesApi = {
  list: (brandId: string, axis?: BrandQueryAxis) => {
    const params = axis ? `?axis=${axis}` : '';
    return api.get<BrandQuery[]>(`/brands/${brandId}/queries${params}`);
  },

  /** Prompt-tag registry for the brand (name + usage count), suggestion source. */
  listTags: (brandId: string) =>
    api.get<{ tags: Array<{ name: string; promptCount: number }> }>(
      `/brands/${brandId}/queries/tags`,
    ),

  create: (
    brandId: string,
    body: {
      axis: BrandQueryAxis;
      text: string;
      level?: 'L1' | 'L2' | 'L3' | 'L4' | 'L5' | 'pending';
      zone?: string;
      language?: string;
      tags?: string[];
    },
  ) => api.post<BrandQuery>(`/brands/${brandId}/queries`, body),

  update: (
    brandId: string,
    queryId: string,
    body: {
      axis: BrandQueryAxis | 'pending';
      text?: string;
      intent?: string;
      complements?: string[];
      topic?: string | null;
      criteria?: string[];
      targetId?: string | null;
      zoneMeta?: object | null;
      language?: string;
      tags?: string[];
      status?: BrandQueryStatus;
      preview?: true;
    },
  ) => api.patch<BrandQuery & { figuresAfter?: AllowanceFigures; refused?: boolean; warnings?: SaveWarnings[]; firstRun?: object[] }>(
    `/brands/${brandId}/queries/${queryId}`,
    body,
  ),

  archive: (brandId: string, axis: BrandQueryAxis | 'pending', queryId: string, reason?: 'deleted') =>
    api.delete<{ ok: true }>(`/brands/${brandId}/queries/${axis}/${queryId}${reason ? `?reason=${reason}` : ''}`),

  regenerate: (
    brandId: string,
    axis: BrandQueryAxis,
    opts?: {
      languages?: string[];
      zones?: string[];
      topics?: string[];
      count?: number;
      promptBudget?: number;
      brandPersonaId?: string;
      /** Comparaison: the competitor names to compare the brand with (omitted → all). */
      competitors?: string[];
      studio?: true;
    },
  ) =>
    api.post<{
      proposals: GeneratedQueryProposal[];
      geoStrategyHash: string;
      remainingQuota: number;
      maxAllowed: number;
    }>(`/brands/${brandId}/queries/regenerate`, {
      axis,
      ...(opts?.languages ? { languages: opts.languages } : {}),
      ...(opts?.zones ? { zones: opts.zones } : {}),
      ...(opts?.topics ? { topics: opts.topics } : {}),
      ...(typeof opts?.count === 'number' ? { count: opts.count } : {}),
      ...(typeof opts?.promptBudget === 'number' ? { promptBudget: opts.promptBudget } : {}),
      ...(opts?.brandPersonaId ? { brandPersonaId: opts.brandPersonaId } : {}),
      ...(opts?.competitors?.length ? { competitors: opts.competitors } : {}),
      ...(opts?.studio ? { studio: true } : {}),
    }),

  /**
   * Edit the per-axis source groups (authority topics → keywords, referral use
   * cases → scenarios). Server saves then auto re-validates Brand + GEO.
   */
  updateTopics: (brandId: string, axis: BrandQueryAxis, items: string[]) =>
    api.patch<{ items: string[]; field: 'keywords' | 'scenarios' | 'decisionCriteria' }>(
      `/brands/${brandId}/queries/${axis}/topics`,
      { items },
    ),

  /** Illustrative example queries (narrative/authority/referral) per authority topic. */
  examples: (brandId: string, topics: string[]) =>
    api.post<{ examples: QueryExampleRow[] }>(`/brands/${brandId}/queries/examples`, { topics }),

  // ---- Prompt Studio (lot A) ----

  /** C3 — create prompts (classify or commit). dryRun=true returns BulkClassified[] + preview. */
  bulk: (brandId: string, body: BulkRequest) =>
    api.post<BulkResponse>(`/brands/${brandId}/queries/bulk`, body),

  /** C11 19a — re-classify a pending/existing prompt. Returns BulkClassified + optional figures. */
  classify: (brandId: string, queryId: string, body: { axis: string; text?: string }) =>
    api.post<BulkClassified & { figuresAfter?: AllowanceFigures; refused?: boolean; warnings?: SaveWarnings[] }>(
      `/brands/${brandId}/queries/${queryId}/classify`,
      body,
    ),

  /** C12 — one natural question per keyword. Shares the generation cooldown. */
  fromSeeds: (brandId: string, seeds: string[]) =>
    api.post<{ proposals: Array<{ seed: string; text?: string }> }>(
      `/brands/${brandId}/queries/from-seeds`,
      { seeds },
    ),

  /** C18 — current allowances and figures for the footer. */
  allowances: (brandId: string) =>
    api.get<{
      promptRequests: AllowanceFigures & { creditsPerMonth: number; cadence: Cadence; nextLaunchAt?: string };
      conversationRuns: Pick<AllowanceFigures, 'allowance' | 'used' | 'consumed' | 'remaining'>;
    }>(`/brands/${brandId}/queries/allowances`),

  /** 00b DQ9 — « dernier passage » per prompt queryId. */
  lastRuns: (brandId: string) =>
    api.get<Record<string, PromptLastRun>>(`/brands/${brandId}/queries/last-runs`),

  // ---- Lot D — language variants ----

  /** Lot D step 3 — translate roots into languages (saves nothing). */
  translateVariants: (brandId: string, body: TranslateVariantsRequest): Promise<TranslateVariantsResponse> =>
    api.post(`/brands/${brandId}/queries/language-variants/translate`, body),

  /** Lot D step 4 — create language variants (or preview). */
  createLanguageVariants: (brandId: string, body: CreateLanguageVariantsRequest): Promise<CreateLanguageVariantsResponse> =>
    api.post(`/brands/${brandId}/queries/language-variants`, body),

  /** Lot E step 10 — add places to the opened row's family (or preview). */
  createZoneVariants: (brandId: string, queryId: string, body: CreateZoneVariantsRequest): Promise<CreateZoneVariantsResponse> =>
    api.post(`/brands/${brandId}/queries/${queryId}/zone-variants`, body),

  applyRegen: (
    brandId: string,
    body: {
      axis: BrandQueryAxis;
      geoStrategyHash?: string;
      items: Array<{ text: string; level?: string; zone?: string; language?: string; topic?: string }>;
    },
  ) =>
    api.post<{
      created: BrandQuery[];
      errors: Array<{ text: string; error: string }>;
    }>(`/brands/${brandId}/queries/regenerate/apply`, body),

  importQueries: (
    brandId: string,
    items: Array<{
      axis: BrandQueryAxis;
      text: string;
      zone?: string;
      language?: string;
      level?: string;
      topic?: string;
      tags?: string[];
    }>,
  ) =>
    api.post<{
      created: BrandQuery[];
      errors: Array<{ index: number; text: string; error: string }>;
    }>(`/brands/${brandId}/queries/import`, { items }),
};
